import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { fetchRecentGymActivityData } from "../../../../redux/prospect-reducers/prospectSlice";
import ReactVortoxSpinner from "../../../common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../../payment-link-system/PaymentLinkTable";
import { formatDateTime } from "../../../common-data/Converts";
import CmnFilters from "../../common/CmnFilters";

const RecentGymActivity = ({ userInfoData, showFilters }) => {
  const dispatch = useDispatch();
  const { recentGymActivity, recentGymActivityLoading } = useSelector(
    (state) => state?.prospects
  );

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  const isAdmin = userInfoData?.account_type === "admin";
  const isGym = userInfoData?.account_type === "gym";
  const isMarketing = userInfoData?.account_type === "marketing";
  const isClusterManager = userInfoData?.account_type === "cluster_manager";

  // Initial filter states
  const iniAllFilters = {
    selectedGym:
      !isAdmin && !isMarketing && !isClusterManager
        ? {
            label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
            value:
              userInfoData?.gym_id ||
              userInfoData?.user_id ||
              userInfoData?.uid ||
              "",
          }
        : "",
    selectedLeadStage: null,
    leadAddDate: [{ from: "", to: "" }],
  };

  const [allFilters, setAllFilters] = useState(iniAllFilters);

  // API filters state
  const iniFilters = {
    page: isPage + 1,
    limit: pageLimit || 10,
    gym_id: iniAllFilters.selectedGym?.value || "",
    lead_stage: iniAllFilters.selectedLeadStage?.value || "",
    date_added_from: iniAllFilters.leadAddDate[0]?.from || "",
    date_added_to: iniAllFilters.leadAddDate[0]?.to || "",
  };

  const [filters, setFilters] = useState(iniFilters);

  // Filter functions
  const onResetFilters = () => {
    setAllFilters(iniAllFilters);
    setIsPage(0);
  };

  const onClickSearch = () => {
    setFilters({
      ...filters,
      gym_id: allFilters.selectedGym?.value || "",
      lead_stage: allFilters.selectedLeadStage?.value || "",
      date_added_from: allFilters.leadAddDate[0]?.from || "",
      date_added_to: allFilters.leadAddDate[0]?.to || "",
      page: 1,
      limit: pageLimit,
    });
    setIsPage(0);
  };

  // Sync filters when allFilters change
  useEffect(() => {
    setFilters({
      page: isPage + 1,
      limit: pageLimit || 10,
      gym_id: allFilters.selectedGym?.value || "",
      lead_stage: allFilters.selectedLeadStage?.value || "",
      date_added_from: allFilters.leadAddDate[0]?.from || "",
      date_added_to: allFilters.leadAddDate[0]?.to || "",
    });
  }, [
    isPage,
    pageLimit,
    allFilters,
  ]);

  // Sync gym_id when userInfoData changes
  // useEffect(() => {
  //   setAllFilters((prevs) => ({
  //     ...prevs,
  //     selectedGym:
  //       !isAdmin && !isMarketing && !isClusterManager
  //         ? {
  //             label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
  //             value:
  //               userInfoData?.gym_id ||
  //               userInfoData?.user_id ||
  //               userInfoData?.uid ||
  //               "",
  //           }
  //         : "",
  //   }));
  // }, [
  //   userInfoData?.gym_id,
  //   isAdmin,
  //   isMarketing,
  //   isClusterManager,
  //   isGym,
  //   userInfoData?.name,
  //   userInfoData?.gym_name,
  //   userInfoData?.user_id,
  //   userInfoData?.uid,
  // ]);

  useEffect(() => {
    if (userInfoData?.token) {
      dispatch(
        fetchRecentGymActivityData({
          filters: filters,
          token: userInfoData?.token,
        })
      );
    }
  }, [dispatch, userInfoData?.token, filters]);

  // Define table columns
  const tableColumns = [
    {
      dataField: "activity",
      text: "Activity",
      formatter: (_, row) => (
        <div className="activity-cell minWidth150">
          <span className={`activity-pill ${
            row?.type === "red_flag" ? "activity-pill-red" : "activity-pill-primary"
          }`}>
            {row?.activity || "-"}
          </span>
        </div>
      ),
    },
    {
      dataField: "customer_name",
      text: "Member",
      formatter: (_, row) => (
        <span className="tb-data minWidth150">{row?.customer_name || "-"}</span>
      ),
    },
    {
      dataField: "mem_lead_stage",
      text: "Stage",
      formatter: (_, row) => {
        const stage = row?.mem_lead_stage || "-";
        
        // Function to get stage pill class
        const getStagePillClass = (stageText) => {
          const stage = stageText.toLowerCase();
          if (stage.includes("fresh")) {
            return "stage-pill stage-pill-fresh";
          } else if (stage.includes("renewal")) {
            return "stage-pill stage-pill-renewal";
          } else if (stage.includes("unpaid")) {
            return "stage-pill stage-pill-unpaid";
          } else if (stage.includes("subscribed")) {
            return "stage-pill stage-pill-subscribed";
          } else {
            return "stage-pill stage-pill-default";
          }
        };
        
        return (
          <span className={getStagePillClass(stage)}>
            {stage}
          </span>
        );
      },
    },
    {
      dataField: "agent_name",
      text: "Agent",
      formatter: (_, row) => (
        <span className="tb-data">{row?.agent_name || "-"}</span>
      ),
    },
    {
      dataField: "gym_name",
      text: "Gym",
      formatter: (_, row) => (
        <span className="tb-data minWidth150">{row?.gym_name || "-"}</span>
      ),
    },
    {
      dataField: "last_follow_up_date",
      text: "Last Follow-up",
      formatter: (_, row) => (
        <span className="tb-data minWidth100">{row?.last_follow_up_date || "N/A"}</span>
      ),
    },
    {
      dataField: "follow_up_count",
      text: "Follow-up Count",
      formatter: (_, row) => (
        <span className="tb-data minWidth100">{row?.follow_up_count || "N/A"}</span>
      ),
    },
    {
      dataField: "follow_up_status",
      text: "Follow-up Status",
      formatter: (_, row) => (
        <span className="tb-data minWidth100">{row?.follow_up_status || "N/A"}</span>
      ),
    },
    {
      dataField: "date_added",
      text: "Date",
      formatter: (_, row) => (
        <span className="tb-data minWidth150">{formatDateTime(row?.date_added) || "-"}</span>
      ),
    },
  ];

  // Transform pagination data for PaymentLinkTable component
  const paginationArray = recentGymActivity?.status
    ? [
        {
          pagination: recentGymActivity?.total || 0,
          pages: recentGymActivity?.totalPages || 1,
        },
      ]
    : [];

  return (
    <div>
      {showFilters && (
        <Row>
          <Col>
            <CmnFilters
              from="recent-gym-activity"
              userInfoData={userInfoData}
              onClickReset={onResetFilters}
              filters={allFilters}
              setFilters={setAllFilters}
              onClickSearch={onClickSearch}
          searchFilterApplied={
            (allFilters.selectedGym?.value !== "" && allFilters.selectedGym?.value !== iniAllFilters.selectedGym?.value) ||
            (allFilters.selectedLeadStage?.value !== "" && allFilters.selectedLeadStage?.value !== null) ||
            (allFilters.leadAddDate[0]?.from !== "" || allFilters.leadAddDate[0]?.to !== "")
          }
            />
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          {recentGymActivityLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 120,
              }}
            >
              <ReactVortoxSpinner />
            </div>
          ) : (
            <div className="fullWidthFlexCenter overflowAuto separatedTable">
              <PaymentLinkTable
                tableData={recentGymActivity?.data || []}
                tableColumns={tableColumns}
                tablePagination={paginationArray}
                isPage={isPage}
                setIsPage={setIsPage}
                pageLimit={pageLimit}
                setPageLimit={setPageLimit}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default RecentGymActivity;
